import { useMemo } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import { AlertRule, AlertRuleType } from '@/api';
import { useIsScrolled } from '@/hooks/useIsScrolled';
import { Loader } from '@/components/Loader';

import { BehavioralAlertsItem } from '../BehavioralAlertsItem/BehavioralAlertsItem';
import { BehavioralAlertsNoData } from '../BehavioralAlertsNoData/BehavioralAlertsNoData';
import { behavioralAlertsDemoData } from './BehavioralAlertsTable.constants';
import styles from './BehavioralAlertsTable.module.sass';
import { DeleteRuleValue } from '../BehavioralAlertsDeleteRuleModal/BehavioralAlertsDeleteRuleModal.constants';

interface BehavioralAlertsTableProps {
  isLoading?: boolean;
  alertRules: AlertRule[];
  activeRuleType?: AlertRuleType;
  onAddRule: () => void;
  onEditRule: (id: AlertRule['id']) => void;
  onDeleteRule: (deletedRule: DeleteRuleValue) => void;
}

export const BehavioralAlertsTable = ({
  isLoading = false,
  alertRules = [],
  activeRuleType = AlertRuleType.COMMON,
  onAddRule,
  onEditRule,
  onDeleteRule,
}: BehavioralAlertsTableProps) => {
  const { t } = useTranslation();
  const isShowDemoData = false;

  const { scrollRef, isScrolled, onScroll } = useIsScrolled({
    delta: 20,
  });

  const filteredRules = useMemo(
    () =>
      (isShowDemoData ? behavioralAlertsDemoData : alertRules ?? []).filter(
        (r) => r.type === activeRuleType,
      ),
    [alertRules, activeRuleType, isShowDemoData],
  );

  const classes = cx(styles.table, {
    [styles['is-scrolled']]: isScrolled,
  });

  const hasData = useMemo(
    () => filteredRules.length > 0 || isShowDemoData,
    [filteredRules, isShowDemoData],
  );

  return (
    <div className={classes}>
      <div className={styles.header}>
        <div className={cx(styles.column, styles.controls)} />
        <div className={cx(styles.column, styles.name)}>
          <span>{t('translation.rule_name')}</span>
        </div>
        <div className={cx(styles.column, styles.amount)}>
          <span>{t('translation.amount_range')}</span>
        </div>
        <div className={cx(styles.column, styles.transfers)}>
          <span>{t('translation.transfers')}</span>
        </div>
        <div className={cx(styles.column, styles.time)}>
          <span>{t('translation.time_period')}</span>
        </div>
        <div className={cx(styles.column, styles.category)}>
          <span>{t('translation.categories')}</span>
        </div>
        <div className={cx(styles.column, styles.grade)}>
          <span>{t('translation.alert_grade')}</span>
        </div>
        <div className={cx(styles.column, styles.actions)} />
      </div>

      <div ref={scrollRef} className={styles.body} onScroll={onScroll}>
        {isLoading && (
          <div className={styles.loader}>
            <Loader size="medium" />
          </div>
        )}

        <div className={styles.list}>
          {!hasData && <BehavioralAlertsNoData onAddRule={onAddRule} />}

          {hasData &&
            filteredRules
              .filter((item) => item?.id)
              .map((item) => (
                <BehavioralAlertsItem
                  key={item?.id}
                  data={item}
                  onDelete={onDeleteRule}
                  onEdit={() => onEditRule(item?.id)}
                />
              ))}
        </div>
      </div>
    </div>
  );
};
